// Spec chipy na kartě výpisu (issue #135, 02-komponenty.md §3).
//
// Návrh chce pod názvem 2–3 hlavní parametry („3,9 kW", „52 cm³", „Postroj"),
// ale v DOM karty NEJSOU: Shoptet parametry na výpisu nerenderuje vůbec. Dotahují
// se proto z našeho vyhledávacího API (`/specs?codes=`) — jeden CORS-simple GET
// na celou stránku, kódy z `data-micro="sku"` každé karty.
//
// KTERÉ parametry se ukážou, rozhoduje kurátorská mapa per kategorie (níže).
// Automat „prvních N dle priority" tu nestačí: napájení 230 V mají všechna
// čerpadla i nabíječky, takže by první chip nic neříkal — a naopak výkon, podle
// kterého se vybírá, leží u některých kategorií až uprostřed seznamu.
//
// Zásada zbytku karty platí i tady: **žádná vymyšlená data**. Co produkt nemá,
// se nekreslí; prázdný ani zástupný chip nevzniká.

import { getIndexConfig } from './searchConfig.js';
import { specsFor } from './specsCache.js';
import { mountSaleEnd } from './cardExtras.js';
import { mountCardRestock } from './restockDate.js';
import { mountSticker } from './productCard.js';
import L from '../locale/cs/specChips.js';
import { slugOf } from './textKey.js';

// názvy parametrů jazyka shopu (klíč párování s indexem, viz locale)
const P = L.params;

/**
 * Mapa kategorie → parametry chipů, v pořadí. Klíč je slug kategorie
 * (první segment cesty), hodnota je 2–3 položky ve tvaru:
 *
 *   'Název INFO parametru'    přesná hodnota z popisu („24 V", „1,65 kW")
 *   'kod-filtracniho'         hodnota z registru; bucketovaná, bere se, až
 *                             když INFO vrstva daný údaj nemá
 *   'klíč|Štítek'             ano/ne parametr — chip nese Štítek místo hodnoty
 *
 * Výběr stojí na pokrytí kategorie (podíl produktů, které parametr mají)
 * a na tom, podle čeho se zboží reálně vybírá; podklad je
 * `docs/ops/parameter-dictionary.md` a change-sety W2. Nová vlna W2 = nový
 * řádek; kategorie, která tu není, spadne na `FALLBACK_INFO_COUNT`.
 */
export const CATEGORY_SPECS = {
  // --- čerpadla (doména sdílí pilot #52) ---
  'kalova-cerpadla-do-septiku--studny': [P.prikon, P.maxPrutok, P.maxVytlak],
  'zahradni-cerpadla': ['prikon', P.maxPrutok, 'max-vytlak'],
  'hlubinna-ponorna-cerpadla': ['prikon', P.maxPrutok, 'max-vytlak'],
  'ponorna-membranova-cerpadla': ['prikon', P.maxPrutok, 'max-vytlak'],
  'benzinova-cerpadla': [P.vykonMotoru, P.maxPrutok, P.maxVytlak],
  'cerpadla-na-topny-olej-a-naftu': [P.maxPrutok, P.prikonVykon, P.maxSaciHloubka],

  // --- aku nářadí ---
  'aku-retezove-pily': [P.delkaListy, P.napeti, P.kapacitaBaterie],
  'aku-krovinorezy': [P.napeti, P.kapacitaBaterie, 'zaber-seceni'],
  // Vrtací šroubováky se vybírají podle napětí platformy a síly; kapacita až třetí
  // (INFO název je „Napětí baterie", ne „Napětí" jako u vlny 3 — aku větev domény
  // elektro nářadí drží jméno filtračního kódu).
  'aku-vrtacky-a-sroubovaky': [P.napetiBaterie, P.krouticiMoment, P.kapacitaBaterie],
  // Brusku vybírá průměr kotouče (125 mm plná / 75–76 mm mini) — bez něj zákazník
  // koupí špatné kotouče; pak napětí platformy a otáčky (INFO nese plné znění
  // rozsahu, filtr jen bucket).
  'aku-brusky': [P.prumerKotouce, P.napetiBaterie, P.otackyNaprazdno],

  // --- benzín a elektro na zahradu ---
  'benzinove-krovinorezy': [P.vykon, P.zdvihovyObjemMotoru, 'popruh-postroj|' + P.postroj],
  'elektricke-sekacky-na-travu': [P.prikonVykon, 'zaber-seceni', 'objem-sberneho-kose'],
  'provzdusnovace-a-vertikulatory-travniku': [P.prikonVykon, 'zaber-seceni', 'objem-sberneho-kose'],

  // --- elektro nářadí: vrtání a bourání (doména W2 blok 3) ---
  'elektricke-vrtacky': [P.prikon, 'typ-upinani', 'priklep|' + P.priklep],
  // Utahováky se vybírají momentem (INFO nese přesné Nm, filtr jen bucket), pak
  // příkonem; třetí je rozměr čtyřhranu — kupuje se k němu sada hlavic.
  'elektricke-razove-utahovaky': [P.krouticiMoment, P.prikon, P.upinani],
  // Kladivo se vybírá upínáním (rozhoduje o kompatibilitě sekáčů a vrtáků, pokrytí
  // 90 %), pak energií úderu (INFO nese přesné J, filtr jen bucket) a hmotností —
  // ta dělí lehká vrtací kladiva do ruky od 15kg bouracích strojů. Příkon je až
  // čtvrtý: 2 600 W má skoro všechno a nerozhoduje.
  'vrtaci-a-sekaci-kladiva': ['typ-upinani', P.energieUderu, P.hmotnost],

  // --- svařování (doména W2 blok 4) ---
  // Svářečku plastů vybírá příkon (je i v názvu produktu), pak sada nástavců —
  // ta rozhoduje, jaké potrubí lze svařit. Třetí je teplota. „Teplotní rozsah"
  // v mapě NENÍ, i když ho INFO u 3 produktů nese: mapa smí mít nejvýš MAX_CHIPS
  // položek a `Max. teplota` pokryje i tyhle tři přes filtrační vrstvu (mají
  // `max-teplota` bucket, ověřeno v dávce #121) — je to celých 18/34 proti 15/34.
  // Cena je jen zhrubnutí u těch tří: „do 300 °C" místo přesného rozsahu.
  'svarecky-plastu-a-plastovych-trubek': [P.prikon, P.prumeryNastavcu, P.maxTeplota],
  // Invertor vybírá svařovací proud (INFO nese rozsah „20–330 A", filtr jen pásmo), pak metoda —
  // ta odděluje MMA stroj od multiprocesního a je to jediný vícehodnotový filtr v mapě
  // (hodnoty přijdou spojené čárkou, „MMA, TIG"). Třetí je průměr elektrody kvůli spotřebnímu
  // materiálu. Zatěžovatel je až čtvrtý: pokrytí 45 % a u 21 z 25 strojů je to shodných 60 %.
  'invertorove-svarecky': [P.svarovaciProud, 'typ-svarovani', P.prumerElektrody],
  // Rodičovská „svarecky" ZÁMĚRNĚ řádek NEMÁ (rozhodnuto v dávce #121, blok 4). Míchá
  // 34 svářeček plastů, 56 invertorů a 8 zbylých kusů včetně plazmové řezačky — dvě větve
  // domény, které nesdílejí ani jeden parametr: příkon ve W × svařovací proud v A, nástavce
  // × elektroda, teplota × zatěžovatel. Jakýkoli společný trojlístek by u poloviny karet
  // zůstal prázdný. Fallback (2 INFO dle priority) dá plastům jejich příkon a invertorům
  // jejich proud, protože pořadí priorit nastavil extraktor per kategorii.

  // --- kabely a prodlužovací přívody (doména W2 blok 4) ---
  // Kabel se vybírá délkou — je to jediná osa, která od sebe odliší jinak shodné
  // produkty (sesterská kategorie se dokonce jmenuje „Kabely 10, 20, 30, 50m"),
  // pokrytí 87 %. Druhý je počet zásuvek (77 %), třetí průřez vodiče: rozhoduje,
  // jestli kabel utáhne stavební nářadí, nebo jen svítilnu. Krytí IP je až čtvrté
  // (33 %) a materiál bubnu by u poloviny karet zůstal prázdný — kategorie míchá
  // ploché kabely s bubny.
  'prodluzovaci-kabely': [P.delkaKabelu, P.pocetZasuvek, P.prurezVodice],
  // Bubny mají vlastní trojlístek, ne kopii rodičovské kategorie: „Počet zásuvek"
  // je tu 4 ks u VŠECH 47 produktů, které ho mají — na výpisu by to byl 47× týž
  // chip, tedy nula informace. Místo něj jde do trojice materiál bubnu (Plast 16 ·
  // Kov 13, 55 %) — jediná osa, kterou tahle kategorie přidává proti plochým
  // kabelům. Pořadí: délka (92 %) · průřez vodiče (64 %, utáhne nářadí × svítilnu)
  // · materiál bubnu.
  'prodluzovaci-kabely-na-bubnu': [P.delkaKabelu, P.prurezVodice, P.materialBubnu],

  // --- dílna a zvedací technika (doména W2 blok 7) ---
  // Fragmenty z dávek 2026-08-20 (blok #124), sloučené integrátorem.
  // Sady vede počet nástrojů (proto je zákazník kupuje) a počet baterií; třetí
  // osa v dávce záměrně není (neúplný facet — viz pravidlo ve slovníku).
  'sady-naradi': [P.pocetNastrojuVSade, P.pocetBaterii],
  'hevery-a-podpery': [P.nosnost, 'typ-produktu', P.minVyska],
  'malirske-jednotky': ['prikon', 'typ-produktu', P.objemNadobky],
  'magnet-fishing': [P.tahMagnetu, 'oboustranny', P.zavitOka],
  'elektricke-navijaky': [P.taznaSilaNosnost, 'napajeni', P.vykonMotoru],

  // --- zahrada II (doména W2 blok 5) ---
  // Fragmenty z dávek 2026-08-20 (blok #122); zdůvodnění pořadí v spec-chips.json
  // jednotlivých change-setů.
  'plastove-obrubniky-kolem-zahonu': [P.delka, P.barva, 'vyska-obrubniku|' + P.vyska],
  'zahradni-hadice': ['delka-hadice', 'prumer-hadice', 'pocet-vrstev'],
  'hadice-pro-cerpadla': ['prumer-hadice', 'delka-hadice', 'pracovni-tlak'],
  'zahradni-kultivatory': ['pohon', P.pracovniSirka, P.pracovniHloubka],
  'zahradni-postrikovace': [P.objemNadrze, 'typ-produktu', 'napajeni'],

  // --- domácnost (doména W2 blok 6) ---
  // Fragment z kanónu domény (ops/changesets/2026-08-20-kanon-domacnost/spec-chips.json).
  // Rodičovská /bazeny/ řádek ZÁMĚRNĚ nemá (vzor rodičovské „svarecky", #121):
  // 9 bazénů + 36 kusů příslušenství nesdílí ani jeden parametr, fallback 2 INFO
  // dle priority obslouží obě půlky.
  'ventilatory': [P.prikon, P.prumerVentilatoru, P.pocetRychlosti],
  'vysavace': [P.prikon, P.objemNadoby, P.saciVykon],
  'bazeny-2': [P.rozmery, P.objemVody, P.material],
  'fritezy': [P.objemNadoby, P.prikon, P.teplotniRozsah],
  'varice': [P.prikon, P.pocetPlotynek, P.objemKartuse],

  // --- ostatní ---
  'nabijecky-autobaterii': [P.nabijeciNapeti, P.nabijeciProud, P.rozsahKapacityBaterie],
  'detske-plastove-skluzavky': ['delka-skluzavky', 'vyska-plosiny', P.maximalniNosnost],
};

// Kategorie bez řádku v mapě: prvních N popisných parametrů dle priority. Pořadí
// jim dal extraktor W2 per kategorie, takže náhodné není — jen není vyladěné.
const FALLBACK_INFO_COUNT = 2;

// Kolik řádků parametrů se vůbec vykreslí. Mobil řeže na 2 přes CSS (m04,
// mockup), tady jde jen o strop celkově.
const MAX_CHIPS = 3;

// Ano/ne parametr: štítek nese název („Postroj"), hodnota potvrzení. Řádkové
// zobrazení redesignu potřebuje obojí — samotné „Postroj" vpravo by viselo.
const LABEL_YES = L.yes;

// Hodnota delší než tohle se do chipu nevejde ani po zkrácení — pak se raději
// nekreslí (rozbitá karta je horší než chip navíc).
const MAX_VALUE_LENGTH = 28;

// Nad tuhle délku se z hodnoty odřízne závorková vsuvka: „1", 1½", 2" (adaptér
// v balení)" → „1", 1½", 2"“. Krátké hodnoty si závorku nechají, protože tam
// nese podstatu („6" (15 cm)").
const TRIM_PARENS_OVER = 20;

const CARD_SELECTOR = '.p[data-micro="product"]';
const CHIPS_CLASS = 'bs-card__specs';

/** Ano/ne hodnoty: chip nese název parametru, „ne“ se nekreslí vůbec. */
function boolValue(value) {
  const v = value.trim().toLowerCase();
  if (v === L.boolYes || v.indexOf(L.boolYes + ' ') === 0 || v.indexOf(L.boolYes + '(') === 0) return true;
  if (v === L.boolNo || v.indexOf(L.boolNo + ' ') === 0 || v.indexOf(L.boolNo + '(') === 0) return false;
  return null;
}

/** „1", 1½", 2" (adaptér v balení)" → „1", 1½", 2"" (jen u dlouhých hodnot). */
function shorten(value) {
  let out = value.trim();
  if (out.length > TRIM_PARENS_OVER) out = out.replace(/\s*\([^)]*\)\s*$/, '').trim();
  return out;
}

/**
 * Klíč pro párování parametrů: „Délka lišty" i kód `delka-listy` musí padnout
 * na totéž. Bez toho by položka mapy zapsaná kódem (`prikon`) minula INFO
 * parametr „Příkon" a chip by ukázal bucket z registru („do 1 000 W") místo
 * přesné hodnoty z popisu („750 W").
 * Počítá ho sdílený `slugOf` (textKey.js).
 */

/** INFO parametr podle slugu názvu — přesná hodnota z popisu produktu. */
function findInfo(info, slug) {
  for (let i = 0; i < info.length; i++) {
    if (slugOf(info[i].name) === slug) return { name: info[i].name, value: info[i].value };
  }
  return null;
}

/**
 * Jedna položka mapy → `{ l, v }` (štítek a hodnota řádku), nebo `null`
 * (produkt parametr nemá).
 *
 * Redesign (`design_handoff_category/`) parametry nekreslí jako chipy, ale jako
 * řádky `štítek — hodnota`, takže je potřeba i název parametru. Štítek je název
 * parametru z dat; `klíč|Štítek` z mapy ho přebíjí zkrácenou kurátorskou verzí
 * (na kartě 2 sloupce se „Napětí baterie" nevejde).
 *
 * INFO vrstva má přednost před filtrační: v registru jsou hodnoty bucketované
 * do pásem („do 1 000 W"), kdežto INFO nese hodnotu tak, jak stojí v popisu.
 * Filtr se bere, až když INFO daný údaj nemá — u legacy kategorií je to jediné,
 * co produkt má.
 */
export function chipFor(key, specs) {
  const parts = String(key).split('|');
  const lookup = parts[0].trim();
  const label = parts.length > 1 ? parts[1].trim() : '';
  if (!lookup) return null;

  const info = (specs && specs.info) || [];
  const filter = (specs && specs.filter) || [];
  const slug = slugOf(lookup);

  let found = findInfo(info, slug);
  if (!found) {
    for (let i = 0; i < filter.length; i++) {
      const f = filter[i];
      if (f.code !== lookup && slugOf(f.code) !== slug && slugOf(f.name) !== slug) continue;
      // Filtr se jmenuje jinak než jeho kód (`prikon` → „Příkon / výkon")? Zkus
      // přes jeho název ještě jednou INFO, než sáhneš po bucketu.
      found = findInfo(info, slugOf(f.name)) || { name: f.name, value: f.value };
      break;
    }
  }
  if (!found || !found.value) return null;

  const bool = boolValue(found.value);
  if (bool === false) return null; // „bez brzdy" není prodejní argument
  // ano/ne: název parametru je celá informace („Postroj — Ano")
  if (bool === true) return { l: label || found.name, v: LABEL_YES };

  const value = shorten(found.value);
  if (!value || value.length > MAX_VALUE_LENGTH) return null;
  return { l: label || found.name, v: value };
}

/** Slug kategorie = první segment cesty (kategorie Jiposu jsou ploché). */
export function categorySlug(pathname) {
  const first = String(pathname || '').split('/').filter(Boolean)[0];
  return first ? first.toLowerCase() : '';
}

/** Parametry produktu → řádky `{ l, v }` dle mapy kategorie (max `MAX_CHIPS`). */
export function chipsFor(specs, slug) {
  const keys = CATEGORY_SPECS[slug];
  const out = [];
  const seen = {};
  const add = function (row) {
    if (!row) return;
    const key = row.l + ' ' + row.v;
    if (seen[key]) return;
    seen[key] = 1;
    out.push(row);
  };

  if (keys) {
    for (let i = 0; i < keys.length && out.length < MAX_CHIPS; i++) {
      add(chipFor(keys[i], specs));
    }
    return out;
  }

  // Bez mapy: prvních N INFO parametrů v pořadí priority.
  const info = (specs && specs.info) || [];
  for (let i = 0; i < info.length && out.length < FALLBACK_INFO_COUNT; i++) {
    const value = shorten(info[i].value || '');
    if (!value || value.length > MAX_VALUE_LENGTH) continue;
    const bool = boolValue(value);
    if (bool === false) continue;
    add({ l: info[i].name, v: bool === true ? LABEL_YES : value });
  }
  return out;
}

/** Kód produktu z karty (`data-micro="sku"`), nebo prázdný řetězec. */
function codeOf(card) {
  const el = card.querySelector('[data-micro="sku"]');
  return el ? (el.textContent || '').trim() : '';
}

/**
 * Vykreslí řádky parametrů do karty. Pozice: hned za krátký popis, jinak za
 * název — v obou případech nad ukazatelem zásoby (ten má `margin-top: auto`).
 */
function render(card, rows) {
  if (!rows.length || card.querySelector('.' + CHIPS_CLASS)) return;

  const box = document.createElement('div');
  box.className = CHIPS_CLASS;
  for (let i = 0; i < rows.length; i++) {
    const row = document.createElement('span');
    row.className = 'bs-card__spec';
    const label = document.createElement('span');
    label.className = 'bs-card__spec-l';
    label.textContent = rows[i].l;
    const value = document.createElement('span');
    value.className = 'bs-card__spec-v';
    value.textContent = rows[i].v;
    row.appendChild(label);
    row.appendChild(value);
    box.appendChild(row);
  }

  const anchor = card.querySelector('.p-desc') || card.querySelector('a.name');
  if (anchor) anchor.insertAdjacentElement('afterend', box);
  else card.appendChild(box);
}

/** Karty, které ještě chipy neřešily (idempotence po loadMore i re-initu). */
function pendingCards(root) {
  const out = [];
  const cards = (root || document).querySelectorAll(CARD_SELECTOR);
  for (let i = 0; i < cards.length; i++) {
    const card = cards[i];
    if (card.dataset.bsSpecs) continue;
    // mini karta (`.products-top`, `.bs-mini`) je na chipy moc malá — návrh je
    // má jen na kartě výpisu; do dotazu ale jde taky, aby konec akční ceny
    // (saleEndCards.js) nedělal druhý GET — chipy se jí nevykreslí (viz fill)
    card.dataset.bsSpecs = '1';
    if (codeOf(card)) out.push(card);
  }
  return out;
}

/** Dotáhne parametry pro dávku karet a vykreslí chipy. Nikdy nehází. */
async function fill(cards, slug) {
  const byCode = new Map();
  for (let i = 0; i < cards.length; i++) {
    const code = codeOf(cards[i]);
    if (!byCode.has(code)) byCode.set(code, []);
    byCode.get(code).push(cards[i]);
  }

  // sdílená cache `/specs` (specsCache.js) — dávkuje sama, API mimo = `null` → karta beze změny
  const items = await specsFor([...byCode.keys()]);
  for (const [code, item] of items) {
    if (!item) continue;
    const targets = byCode.get(code);
    if (!targets) continue;
    const texts = chipsFor(item, slug);
    for (let t = 0; t < targets.length; t++) {
      const mini = !!targets[t].closest('.products-top, .bs-mini');
      if (!mini) render(targets[t], texts);
      // konec akční ceny ze STEJNÉ odpovědi — segment pilulky (cardExtras.js),
      // ať výpis nedělá druhý GET; kreslí se jen u karty s přeškrtnutou cenou
      if (item.action && item.action.to) mountSaleEnd(targets[t], item.action.to);
      // Bestseller ze STEJNÉ odpovědi (top výchozí kategorie dle tržby, ne TIP)
      if (item.bestseller === true && !mini) mountSticker(targets[t]);
      // termín naskladnění vyprodané karty (restockDate.js) ze STEJNÉ odpovědi
      mountCardRestock(targets[t], item);
    }
  }
}

/**
 * Spec chipy na výpisu kategorie. Degraduje tiše na všech úrovních: mimo
 * kategorii, bez konfigurace indexu (Ú0 — např. na stránce běží Luigi's Box)
 * i při chybě API zůstane karta přesně taková, jaká byla. Parametry cenu nenesou,
 * jedou proto i velkoobchodu; konec akce z téže odpovědi hlídá `mountSaleEnd`.
 */
export function initSpecChips() {
  if (!document.body.classList.contains('type-category')) return;
  if (!getIndexConfig()) return;

  const slug = categorySlug(window.location.pathname);
  const run = (root) => {
    const cards = pendingCards(root);
    if (cards.length) fill(cards, slug);
  };

  run(document);

  // loadMore dosypává karty AJAXem a ShoptetDOM* eventy u něj nestřílí
  document.querySelectorAll('.products').forEach(function (block) {
    if (block.dataset.bsSpecsObserved) return;
    block.dataset.bsSpecsObserved = '1';
    new MutationObserver(function (muts) {
      let touched = false;
      muts.forEach(function (mu) {
        mu.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) touched = true;
        });
      });
      if (touched) run(block);
    }).observe(block, { childList: true, subtree: true });
  });
}
